import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearCart } from '../../store/cartSlice';

const OrderSuccess = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const preferenceId = searchParams.get('preference_id');
    const dispatch = useDispatch();

    const { orderNumber: fallbackOrderNumber } = location.state || { orderNumber: 'N/A' };
    
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // 1. Vaciar el carrito de Redux siempre que se llegue a esta pantalla
        dispatch(clearCart());

        // 2. Si no hay preferenceId (por ejemplo, pago en efectivo o error), no buscar
        if (!preferenceId) {
            setLoading(false);
            return;
        }

        // 3. Buscar la orden por preferenceId (con reintentos porque el webhook puede demorar 1-2 segundos)
        const fetchOrder = async (retries = 3) => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/orders/by-preference/${preferenceId}`);
                if (res.ok) {
                    const data = await res.json();
                    setOrder(data);
                    setLoading(false);
                } else if (retries > 0) {
                    setTimeout(() => fetchOrder(retries - 1), 2000); // Reintentar en 2s
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error fetching order:', error);
                if (retries > 0) {
                    setTimeout(() => fetchOrder(retries - 1), 2000);
                } else {
                    setLoading(false);
                }
            }
        };

        fetchOrder();
    }, [dispatch, preferenceId]);

    const handleGoHome = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="container wrapper mt-5 text-center" style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <h3 className="mb-4 text-success">
                    <i className="fa-solid fa-circle-check me-2"></i>Pago Aprobado
                </h3>
                <h5 className="text-muted">Procesando tu pedido, por favor espera un momento...</h5>
                <div className="mt-4">
                    <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        );
    }

    const displayOrderNumber = order ? `#${order.orderNumber}` : fallbackOrderNumber;

    return (
        <div className="container wrapper mt-5 mb-5" style={{ maxWidth: '800px' }}>
            <div className="card shadow-lg border-0 rounded-4 overflow-hidden">
                <div className="bg-success text-white text-center py-5">
                    <i className="fa-solid fa-circle-check" style={{ fontSize: '4rem', marginBottom: '1rem' }}></i>
                    <h1 className="fw-bold mb-0">¡Pedido Confirmado!</h1>
                    <p className="lead mt-2 mb-0">Gracias por tu compra. Tu pago se ha procesado correctamente.</p>
                </div>
                
                <div className="card-body p-4 p-md-5 bg-light text-center">
                    <h5 className="text-muted mb-3">Tu número de orden es:</h5>
                    <div className="display-3 fw-bold text-dark mb-4">{displayOrderNumber}</div>
                    
                    {order && (
                        <div className="text-start bg-white p-4 rounded-4 shadow-sm border mt-4">
                            <h5 className="fw-bold mb-4 border-bottom pb-2">Resumen de tu compra</h5>
                            {order.shipping && (
                                <div className="alert alert-success text-start">
                                    <i className="fa-solid fa-location-dot me-2"></i>
                                    <strong>{order.shipping.label}</strong>
                                    {order.shipping.pickupLocation && (
                                        <div className="mt-1 ms-4">
                                            {order.shipping.pickupLocation.name} · {order.shipping.pickupLocation.city}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <div className="table-responsive">
                                <table className="table table-borderless align-middle mb-0">
                                    <tbody>
                                        {order.items.map((item, index) => (
                                            <tr key={index} className="border-bottom">
                                                <td className="ps-0 py-3">
                                                    <h6 className="mb-0 fw-bold">{item.name}</h6>
                                                    <small className="text-muted">Cantidad: {item.quantity}</small>
                                                </td>
                                                <td className="text-end pe-0 py-3 fw-bold text-success">
                                                    ${item.price * item.quantity}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td className="ps-0 pt-4 pb-0 text-end fw-bold fs-5">Total Pagado:</td>
                                            <td className="pe-0 pt-4 pb-0 text-end fw-bold fs-4 text-success">${order.total}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                    
                    <div className="mt-5">
                        <button 
                            className="btn btn-dark btn-lg px-5 py-3 rounded-pill shadow-sm" 
                            onClick={handleGoHome}
                            style={{ transition: 'all 0.3s ease' }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            <i className="fa-solid fa-house me-2"></i> Volver a la Tienda
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderSuccess;